<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import ProjectModal from '../components/ProjectModal.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, Navigation } from 'swiper/modules';
import { projects } from '../utils/projectData';

import 'swiper/swiper-bundle.css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const showModal = ref(false);
const selectedProject = ref(null);
const isMobile = ref(window.innerWidth < 768);
const router = useRouter();

const onResize = () => isMobile.value = window.innerWidth < 768;

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const openModal = (project) => {
  isMobile.value
    ? router.push({ name: 'ProjectDetailView', params: { id: project.id } })
    : (selectedProject.value = project, showModal.value = true);
};
</script>

<template>
  <section class="projects-section">
    <div class="blob blob-1" />
    <div class="blob blob-2" />

    <div class="projects-header">
      <span class="label">What I've built</span>
      <h2>Projects</h2>
    </div>

    <div class="slider-container">
      <Swiper
        v-if="projects.length"
        :slides-per-view="isMobile ? 1 : 3"
        :space-between="24"
        :navigation="true"
        :modules="[Pagination, Navigation]"
        :pagination="{ clickable: true }"
      >
        <SwiperSlide v-for="project in projects" :key="project.id">
          <div class="project-card" @click="openModal(project)">
            <img :src="project.image" :alt="project.title" />
            <div class="project-overlay">
              <h3>{{ project.title }}</h3>
              <p>{{ project.subtitle }}</p>
              <span class="view-btn">View project →</span>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>

    <ProjectModal
      v-if="showModal && !isMobile"
      :project="selectedProject"
      @close="showModal = false"
    >
      <div class="modal-footer">
        <button
          @click="router.push({ name: 'ProjectDetailView', params: { id: selectedProject.id } })"
          class="full-page-btn"
        >
          See full page
        </button>
      </div>
    </ProjectModal>
  </section>
</template>

<style scoped>
.projects-section {
  position: relative;
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto 100px;
  overflow: hidden;
  text-align: center;
}

/* Blobs */
.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.blob-1 {
  width: 400px; height: 400px;
  background: rgba(102, 126, 234, 0.15);
  top: -100px; left: -100px;
}
.blob-2 {
  width: 300px; height: 300px;
  background: rgba(118, 75, 162, 0.15);
  bottom: 0; right: -100px;
}

/* Header */
.projects-header {
  position: relative;
  z-index: 1;
  margin-bottom: 3rem;
}

.label {
  font-size: 0.8rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #667eea;
  font-weight: 600;
}

h2 {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  letter-spacing: -2px;
  background: linear-gradient(-45deg, #667eea, #764ba2, #a8c0ff, #c471ed);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite;
  margin-top: 0.5rem;
}

@keyframes gradientShift {
  0%   { background-position: 0% 50% }
  50%  { background-position: 100% 50% }
  100% { background-position: 0% 50% }
}

/* Slider */
.slider-container {
  position: relative;
  z-index: 1;
  width: 100%;
  padding-bottom: 3rem;
}

/* Card */
.project-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  aspect-ratio: 4/3;
  transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
}

.project-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
  border-color: rgba(102, 126, 234, 0.4);
}

.project-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.project-card:hover img {
  transform: scale(1.08);
}

/* Overlay */
.project-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(10, 10, 30, 0.95) 0%, rgba(10, 10, 30, 0.3) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  text-align: left;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.project-card:hover .project-overlay {
  opacity: 1;
}

.project-overlay h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.3rem;
  letter-spacing: -0.5px;
}

.project-overlay p {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.8rem;
}

.view-btn {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: #667eea;
  text-transform: uppercase;
}

/* Swiper overrides */
:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  color: white;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  backdrop-filter: blur(10px);
  transition: all 0.3s;
}

:deep(.swiper-button-prev):hover,
:deep(.swiper-button-next):hover {
  background: rgba(102, 126, 234, 0.3);
  border-color: #667eea;
}

:deep(.swiper-button-prev)::after,
:deep(.swiper-button-next)::after {
  font-size: 14px;
}

:deep(.swiper-pagination-bullet) {
  background: rgba(255, 255, 255, 0.3);
}

:deep(.swiper-pagination-bullet-active) {
  background: #667eea;
}

@media (max-width: 768px) {
  .project-card { aspect-ratio: 3/2 }
  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) { display: none }
}
</style>